// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, Toolbar, Typography } from '@mui/material';
import { TextInputLabel } from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useExecutedCollateralTransfer } from '../../../hooks/queries/collateral/collateral/useExecutedCollateralTransfer';
import { fmt } from '../../../utils/common';
import { CollateralAgreementRow } from '../collaterals/types';

interface SettlementHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRow?: CollateralAgreementRow;
}
export const SettlementHistoryDialog: React.FC<SettlementHistoryDialogProps> = ({
  open,
  onClose,
  selectedRow,
}) => {
  const settledCollateral = useExecutedCollateralTransfer();
  const party = useModuleParty();
  if (!selectedRow) {
    return null;
  }

  const { data } = settledCollateral;
  const filtered = data?.filter((d) => d.payload.agreementId === selectedRow.payload.id).reverse();
  const rows = filtered?.map((d) => {
    const date = new Date(d.createdAt).toLocaleDateString('en-CA', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}
        key={d.contractId}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {d.payload.settledPositions.map((p) => {
            const isPledgor = p.pledgor === party;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }} key={p.instrument.id}>
                <Typography variant="body2">{date}</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      color: (t) => (isPledgor ? undefined : t.palette.success.main),
                    }}
                  >
                    {isPledgor ? '-' : '+'}
                  </Typography>
                  <Typography
                    sx={{
                      color: (t) => (isPledgor ? undefined : t.palette.success.main),
                    }}
                    variant="body2"
                  >
                    {fmt(p.amount, 2)}
                  </Typography>
                  <Typography
                    sx={{
                      ml: 1,
                      color: (t) => (isPledgor ? undefined : t.palette.success.main),
                    }}
                    variant="body2"
                  >
                    {p.instrument.id}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  });
  return (
    <Dialog open={open} onClose={onClose}>
      <Toolbar>
        <Typography variant="h4" fontWeight="bold">
          Settlement History
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <IconButton size="small" onClick={onClose}>
            <Close color="action" fontSize="small" />
          </IconButton>
        </Box>
      </Toolbar>
      <Box sx={{ pl: 2, pr: 2, pb: 2, minWidth: '400px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextInputLabel label="Date" />
          <Box sx={{ ml: 'auto' }}>
            <TextInputLabel label="Amount" />
          </Box>
        </Box>
        {rows?.length ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', maxHeight: '400px', overflowY: 'auto' }}
          >
            {rows}
          </Box>
        ) : (
          <Typography variant="body2">No history</Typography>
        )}
      </Box>
    </Dialog>
  );
};
